import React, { useState } from 'react';
import {
  Input,
  Switch,
  Slider,
  Select,
  Button,
  message,
  Space,
  Divider,
} from 'antd';
import { connect } from 'dva';
import { StoreState } from '@src/interfaces';

const { Option } = Select;

interface BackgroundImageControlsProps {
  dispatch?: any;
  backgroundImage?: {
    url?: string;
    size?: string;
    position?: string;
    repeat?: string;
    overlay?: boolean;
    overlayOpacity?: number;
    enabled?: boolean;
    emphasizeNav?: boolean;
  };
}

const BackgroundImageControls: React.FC<BackgroundImageControlsProps> = ({
  dispatch,
  backgroundImage = {},
}) => {
  const [url, setUrl] = useState(backgroundImage.url || '');
  const [size, setSize] = useState(backgroundImage.size || 'cover');
  const [position, setPosition] = useState(
    backgroundImage.position || 'center'
  );
  const [repeat, setRepeat] = useState(backgroundImage.repeat || 'no-repeat');
  const [overlay, setOverlay] = useState(backgroundImage.overlay || false);
  const [overlayOpacity, setOverlayOpacity] = useState(
    backgroundImage.overlayOpacity || 0.3
  );
  const [enabled, setEnabled] = useState(backgroundImage.enabled || false);
  const [emphasizeNav, setEmphasizeNav] = useState(
    backgroundImage.emphasizeNav ?? true
  );

  const handleSave = () => {
    if (enabled && !url.trim()) {
      message.error('Please enter a valid image URL');
      return;
    }

    const settings = {
      url: url.trim(),
      size,
      position,
      repeat,
      overlay,
      overlayOpacity,
      enabled,
      emphasizeNav,
    };

    dispatch({
      type: 'settings/backgroundImage',
      payload: settings,
    });

    // Save to persistent storage
    dispatch({
      type: 'settings/saveSetting',
      payload: {
        name: 'backgroundImage',
        settings,
      },
    });
  };

  const handleReset = () => {
    setUrl('');
    setSize('cover');
    setPosition('center');
    setRepeat('no-repeat');
    setOverlay(false);
    setOverlayOpacity(0.3);
    setEnabled(false);
    setEmphasizeNav(true);
  };

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      // Clear background when disabled
      dispatch({
        type: 'settings/backgroundImage',
        payload: { enabled: false },
      });
    }
  };

  const validateUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <div className='gx-customizer-item'>
      <h6 className='gx-mb-3 gx-text-uppercase'>Hình nền</h6>

      <Space direction='vertical' style={{ width: '100%' }} size='middle'>
        <div>
          <label>Cho phép hiển thị hình nền</label>
          <Switch checked={enabled} onChange={handleToggle} />
        </div>

        {enabled && (
          <>
            <div>
              <label>Đường dẫn hình ảnh</label>
              <Input
                placeholder='Enter image URL (e.g., https://example.com/image.jpg)'
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onBlur={() => {
                  if (url && !validateUrl(url)) {
                    message.warning('Please enter a valid URL');
                  }
                }}
              />
            </div>

            <div>
              <label>Kích thước hình ảnh</label>
              <Select value={size} onChange={setSize} style={{ width: '100%' }}>
                <Option value='cover'>Cover (Lấp đầy màn hình)</Option>
                <Option value='contain'>Contain (Phù hợp với màn hình)</Option>
                <Option value='auto'>Auto (Kích thước gốc)</Option>
                <Option value='100%'>100% (Kích thước đầy màn hình)</Option>
              </Select>
            </div>

            <div>
              <label>Vị trí hình ảnh</label>
              <Select
                value={position}
                onChange={setPosition}
                style={{ width: '100%' }}
              >
                <Option value='center'>Giữa</Option>
                <Option value='top'>Trên</Option>
                <Option value='bottom'>Dưới</Option>
                <Option value='left'>Trái</Option>
                <Option value='right'>Phải</Option>
                <Option value='top left'>Trên trái</Option>
                <Option value='top right'>Trên phải</Option>
                <Option value='bottom left'>Dưới trái</Option>
                <Option value='bottom right'>Dưới phải</Option>
              </Select>
            </div>

            <div>
              <label>Lặp lại hình ảnh</label>
              <Select
                value={repeat}
                onChange={setRepeat}
                style={{ width: '100%' }}
              >
                <Option value='no-repeat'>Không lặp lại</Option>
                <Option value='repeat'>Lặp lại</Option>
                <Option value='repeat-x'>Lặp lại theo chiều ngang</Option>
                <Option value='repeat-y'>Lặp lại theo chiều dọc</Option>
              </Select>
            </div>

            <Divider />

            <div>
              <label>Nhấn mạnh Navigation</label>
              <Switch checked={emphasizeNav} onChange={setEmphasizeNav} />
            </div>

            <div>
              <label>Thêm lớp đen (chỉ áp dụng cho nội dung)</label>
              <Switch checked={overlay} onChange={setOverlay} />
            </div>

            {overlay && (
              <div>
                <label>Độ trong suốt lớp đen: {overlayOpacity}</label>
                <Slider
                  min={0.1}
                  max={0.8}
                  step={0.1}
                  value={overlayOpacity}
                  onChange={setOverlayOpacity}
                  tipFormatter={(v) => `${Math.round((v as number) * 100)}%`}
                />
              </div>
            )}

            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button onClick={handleReset}>Đặt lại</Button>
              <Button type='primary' onClick={handleSave}>
                Lưu thiết lập
              </Button>
            </Space>
          </>
        )}
      </Space>
    </div>
  );
};

const mapStateToProps = ({ settings }: StoreState) => {
  const { backgroundImage } = settings;
  return { backgroundImage };
};

export default connect(mapStateToProps)(BackgroundImageControls);
